import React, { useEffect, useMemo, useState } from "react";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip } from "recharts";
import { api } from "../lib/api";
import { trackClick } from "../lib/analytics";
import { AlertTriangle, Check, Zap, WifiOff, LogIn, AppWindow, HelpCircle, Bell } from "lucide-react";

const ISSUES = [
  { key: "cant-connect", label: "Can't connect", Icon: WifiOff },
  { key: "slow", label: "Slow / loading", Icon: Zap },
  { key: "login", label: "Login problem", Icon: LogIn },
  { key: "app-crash", label: "App crashing", Icon: AppWindow },
  { key: "other", label: "Something else", Icon: HelpCircle },
];

function alreadyReported(slug) {
  const key = `stt.reported.${slug}`;
  const ts = parseInt(localStorage.getItem(key) || "0", 10);
  return ts && Date.now() - ts < 30 * 60 * 1000;
}

export function ReaderReports({ slug, serviceName, initial1h = 0, initial24h = 0, testid }) {
  const [data, setData] = useState(null);
  const [reports1h, setReports1h] = useState(initial1h);
  const [reports24h, setReports24h] = useState(initial24h);
  const [reported, setReported] = useState(() => alreadyReported(slug));
  const [posting, setPosting] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get(`/services/${slug}/reports`).then((r) => {
      if (!cancelled) setData(r.data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [slug]);

  const submit = async (issueKey) => {
    if (reported || posting) return;
    setErr(null);
    setPosting(true);
    trackClick("service-report", { slug, issue_type: issueKey });
    try {
      const r = await api.post(`/services/${slug}/report`, { issue_type: issueKey });
      setReports1h(r.data.reports_1h || 0);
      setReports24h(r.data.reports_24h || 0);
      localStorage.setItem(`stt.reported.${slug}`, String(Date.now()));
      setReported(true);
      // reload chart
      const r2 = await api.get(`/services/${slug}/reports`);
      setData(r2.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't submit — try again.");
    } finally {
      setPosting(false);
    }
  };

  const peak = useMemo(() => (data?.series || []).reduce((m, p) => Math.max(m, p.reports), 0), [data]);
  const hasSpike = reports1h >= 5;

  return (
    <section className="rounded-xl p-4 border" style={{ background: hasSpike ? "rgba(248,113,113,0.06)" : "rgba(61,107,255,0.05)", borderColor: hasSpike ? "rgba(248,113,113,0.30)" : "rgba(61,107,255,0.22)" }} data-testid={testid || "reader-reports"}>
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <AlertTriangle className="w-4 h-4 shrink-0" style={{ color: hasSpike ? "#F87171" : "var(--cobalt-bright)" }} />
          <h4 className="font-display text-sm font-semibold truncate">Reader reports</h4>
        </div>
        <div className="text-[11px] font-mono text-[var(--text-muted)] flex items-center gap-2">
          <span><b className={hasSpike ? "text-rose-300" : "text-white"}>{reports1h}</b> in last hour</span>
          <span>·</span>
          <span><b className="text-white">{reports24h}</b> in 24 h</span>
        </div>
      </div>

      {/* Chart */}
      {data && data.series && (
        <div className="w-full h-20 mb-3">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.series} margin={{ top: 5, right: 0, left: 0, bottom: 0 }} barCategoryGap={2}>
              <XAxis dataKey="hour" tick={{ fill: "#5A6487", fontSize: 9 }} interval={5} axisLine={false} tickLine={false} />
              <YAxis hide domain={[0, Math.max(peak, 5)]} />
              <Tooltip contentStyle={{ background: "#0F1120", border: "1px solid rgba(138,160,255,0.2)", borderRadius: 6, fontSize: 11 }} labelStyle={{ color: "#8892B8" }} />
              <Bar dataKey="reports" fill={hasSpike ? "#F87171" : "#5C85FF"} radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {reported ? (
        <div className="flex items-center gap-2 text-sm text-emerald-300" data-testid="reader-reports-thanks">
          <Check className="w-4 h-4" /> Thanks for the heads up. You can report again in 30 minutes.
        </div>
      ) : (
        <>
          <div className="text-xs text-[var(--text-dim)] mb-2">Having issues with {serviceName || "this service"}?</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
            {ISSUES.map((i) => (
              <button
                key={i.key}
                onClick={() => submit(i.key)}
                disabled={posting}
                data-testid={`report-btn-${i.key}`}
                className="flex items-center gap-2 px-2.5 py-2 rounded-md text-xs font-medium text-left border border-white/10 bg-white/5 hover:bg-[rgba(61,107,255,0.12)] hover:border-[rgba(61,107,255,0.35)] transition disabled:opacity-50"
              >
                <i.Icon className="w-3.5 h-3.5 shrink-0 text-[var(--cobalt-bright)]" />
                <span className="truncate">{i.label}</span>
              </button>
            ))}
          </div>
          {err && <div className="text-xs text-rose-300 mt-2">{err}</div>}
        </>
      )}

      {/* Subscribe */}
      <SubscribeInline slug={slug} serviceName={serviceName} />
    </section>
  );
}

function SubscribeInline({ slug, serviceName }) {
  const localKey = `stt.subscribed.${slug}`;
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [state, setState] = useState(() => localStorage.getItem(localKey) ? "sent" : "idle");
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (state === "sending") return;
    setErr(null);
    setState("sending");
    trackClick("service-subscribe", { slug });
    try {
      await api.post(`/services/${slug}/subscribe`, { email: email.trim().toLowerCase() });
      localStorage.setItem(localKey, "1");
      setState("sent");
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Couldn't sign you up — try again.");
      setState("idle");
    }
  };

  if (state === "sent") {
    return (
      <div className="mt-3 pt-3 border-t border-white/8 text-xs text-emerald-300 flex items-center gap-2" data-testid="subscribe-sent">
        <Check className="w-3.5 h-3.5" /> Check your email to confirm — we'll ping you when {serviceName} has spikes.
      </div>
    );
  }

  return (
    <div className="mt-3 pt-3 border-t border-white/8" data-testid="subscribe-block">
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          className="text-xs text-[var(--cobalt-bright)] hover:text-white transition inline-flex items-center gap-1.5"
          data-testid="subscribe-open"
        >
          <Bell className="w-3 h-3" /> Alert me next time {serviceName} has issues
        </button>
      ) : (
        <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2" data-testid="subscribe-form">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            className="flex-1 min-w-0 bg-[rgba(15,17,32,0.7)] border border-white/10 rounded-md px-3 py-1.5 text-xs focus:outline-none focus:border-[var(--cobalt)] transition"
            data-testid="subscribe-email-input"
          />
          <button
            type="submit"
            disabled={state === "sending" || !email.trim()}
            className="btn-cobalt inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium disabled:opacity-60"
            data-testid="subscribe-submit"
          >
            <Bell className="w-3 h-3" /> {state === "sending" ? "Sending…" : "Alert me"}
          </button>
          {err && <div className="text-xs text-rose-300 sm:basis-full">{err}</div>}
        </form>
      )}
      <p className="text-[10px] font-mono text-[var(--text-muted)] mt-2">
        No spam · one email when this service spikes · unsubscribe in one click
      </p>
    </div>
  );
}
